import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { 
  Calendar,
  User,
  Tag,
  Clock,
  Save,
  X,
  Loader2,
  Edit3
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { taskService, CreateTaskRequest } from '@/services/task.service';
import { getEmployeesForDropdown } from '@/services/employee.service';
import { useAuthStore } from '@/stores/authStore';
import { getStatusesByCategory } from '@/services/status.service';

type TaskStatusValue = string | { name?: string; status?: string } | null;

interface EditTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task: {
    id: string;
    name: string;
    description?: string;
    status: TaskStatusValue;
    priority: string;
    startDate: string;
    dueDate: string;
    assignedTo: {
      name: string;
      initials: string;
      avatar?: string | null;
    };
    assignees?: Array<{
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    }>;
    followers?: Array<{
      _id: string;
      firstName: string;
      lastName: string;
      email: string;
    }>;
    tags: string[];
    estimatedHours: number;
    url?: string;
    projectId?: string;
  } | null;
  onTaskUpdate?: (taskId: string, updates: any) => void;
}

const priorityOptions = [
  { value: 'Low', label: 'Low', color: 'bg-green-100 text-green-800' },
  { value: 'Medium', label: 'Medium', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'High', label: 'High', color: 'bg-red-100 text-red-800' }
];

// Fallback status options (will be replaced by API statuses if available)
const FALLBACK_STATUS_OPTIONS = [
  { value: 'NOT_STARTED', label: 'Not Started' },
  { value: 'IN_PROGRESS', label: 'In Progress' },
  { value: 'TESTING', label: 'Testing' },
  { value: 'AWAITING_FEEDBACK', label: 'Awaiting Feedback' },
  { value: 'READY_FOR_PRODUCTION', label: 'Ready for Production' },
  { value: 'ON_HOLD', label: 'On Hold' },
  { value: 'COMPLETE', label: 'Complete' }
];

export function EditTaskDialog({ 
  open, 
  onOpenChange, 
  task, 
  onTaskUpdate 
}: EditTaskDialogProps) {
  const { toast } = useToast();
  const { hasPermission } = useAuthStore();
  const [isLoading, setIsLoading] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [statusIdMap, setStatusIdMap] = useState<Map<string, string>>(new Map()); // Map status value to MongoDB ID
  const [statusOptions, setStatusOptions] = useState(FALLBACK_STATUS_OPTIONS); // Dynamic status options from API

  type StatusValue = string | { name?: string; status?: string } | null | undefined;
  type TagLike = string | { _id?: string; id?: string; name?: string; title?: string };
  const tagToString = (tag: TagLike): string =>
    typeof tag === 'string'
      ? tag
      : (tag?._id || tag?.id || tag?.name || tag?.title || '');
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    status: '',
    priority: '',
    startDate: '',
    dueDate: '',
    estimateHours: 0,
    url: '',
    assignees: [] as string[],
    followers: [] as string[],
    tags: [] as TagLike[],
    newTag: ''
  });

  // Helper function to normalize status for matching (handles spaces, underscores, case)
  const normalizeStatusForMatching = (status: string): string => {
    if (!status) return '';
    return status
      .toLowerCase()
      .replace(/_/g, ' ')  // Replace underscores with spaces
      .replace(/\s+/g, ' ')  // Normalize multiple spaces
      .trim();
  };

  // Helper function to match task status to statusOptions value
  const matchStatusToOption = (taskStatus: StatusValue): string => {
    if (!taskStatus) return 'NOT_STARTED';
    
    // Extract status name if it's an object
    let statusValue = '';
    if (typeof taskStatus === 'string') {
      statusValue = taskStatus;
    } else if (typeof taskStatus === 'object') {
      statusValue = taskStatus?.name || taskStatus?.status || '';
    }
    if (!statusValue) return 'NOT_STARTED';
    
    const normalizedTaskStatus = normalizeStatusForMatching(String(statusValue));
    
    // Try to find matching option
    const matchedOption = statusOptions.find(opt => {
      const normalizedOptionValue = normalizeStatusForMatching(opt.value);
      const normalizedOptionLabel = normalizeStatusForMatching(opt.label);
      return normalizedOptionValue === normalizedTaskStatus || 
             normalizedOptionLabel === normalizedTaskStatus ||
             opt.value.toUpperCase() === String(statusValue).toUpperCase() ||
             opt.label.toLowerCase() === String(statusValue).toLowerCase();
    });
    
    if (matchedOption) {
      console.log('Status matched:', statusValue, '->', matchedOption.value);
      return matchedOption.value;
    }
    
    // If no match found, try common mappings
    const statusUpper = String(statusValue).toUpperCase();
    const statusMap: Record<string, string> = {
      'NOT STARTED': 'NOT_STARTED',
      'NOT_STARTED': 'NOT_STARTED',
      'IN PROGRESS': 'IN_PROGRESS',
      'IN_PROGRESS': 'IN_PROGRESS',
      'TESTING': 'TESTING',
      'AWAITING FEEDBACK': 'AWAITING_FEEDBACK',
      'AWAITING_FEEDBACK': 'AWAITING_FEEDBACK',
      'READY FOR PRODUCTION': 'READY_FOR_PRODUCTION',
      'READY_FOR_PRODUCTION': 'READY_FOR_PRODUCTION',
      'ON HOLD': 'ON_HOLD',
      'ON_HOLD': 'ON_HOLD',
      'COMPLETE': 'COMPLETE',
      'COMPLETED': 'COMPLETE',
    };
    
    const mappedStatus = statusMap[statusUpper];
    if (mappedStatus) {
      console.log('Status mapped:', statusValue, '->', mappedStatus);
      return mappedStatus;
    }
    
    console.warn('Status not matched:', statusValue, 'Available options:', statusOptions);
    return 'NOT_STARTED'; // Default fallback
  };

  // Load task data when dialog opens
  useEffect(() => {
    if (open && task) {
      console.log('Loading task data:', task);
      
      // Handle assignees - check both assignees array and assignedTo single object
      let assigneeIds: string[] = [];
      if (task.assignees && Array.isArray(task.assignees)) {
        assigneeIds = task.assignees
          .map((a: any) => {
            if (typeof a === 'string') return a; // could be id or name
            return a._id || a.id || undefined;
          })
          .filter(Boolean) as string[];
      }
      
      // Handle followers
      let followerIds: string[] = [];
      if (task.followers && Array.isArray(task.followers)) {
        followerIds = task.followers
          .map((f: any) => {
            if (typeof f === 'string') return f; // could be id or name
            return f._id || f.id || undefined;
          })
          .filter(Boolean) as string[];
      }
      
      // Match status to option value
      const matchedStatus = matchStatusToOption(task.status as StatusValue);
      
      console.log('Assignee IDs:', assigneeIds);
      console.log('Follower IDs:', followerIds);
      console.log('Task status:', task.status, 'Matched to:', matchedStatus);
      
      // Get estimated hours - check both property name variations 
      // (estimateHours from API, estimatedHours from UI mapping)
      const taskAny = task as any;
      const estimatedHoursValue = taskAny.estimateHours ?? task.estimatedHours;
      const parsedEstimatedHours = typeof estimatedHoursValue === 'number'
        ? estimatedHoursValue
        : Number(estimatedHoursValue ?? 0) || 0;
      
      console.log('Loading task data - Estimated Hours:', {
        'task.estimatedHours': task.estimatedHours,
        'taskAny.estimateHours': taskAny.estimateHours,
        'parsedValue': parsedEstimatedHours,
        'full task object': task
      });
      
      setFormData({
        title: task.name || '',
        description: task.description || '',
        status: matchedStatus, // Use matched status value
        priority: task.priority || 'Medium',
        startDate: task.startDate ? new Date(task.startDate).toISOString().split('T')[0] : '',
        dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
        estimateHours: parsedEstimatedHours,
        url: task.url || '',
        assignees: assigneeIds,
        followers: followerIds,
        tags: (task.tags || []) as TagLike[],
        newTag: ''
      });
    }
  }, [open, task]);

  // Load task statuses from API
  useEffect(() => {
    const loadTaskStatuses = async () => {
      try {
        const statuses = await getStatusesByCategory('task');
        const activeStatuses = statuses.filter(s => s.isActive);

        // Create status options from API statuses
        // Use normalized code as value for matching with existing logic
        const apiStatusOptions = activeStatuses.map(status => {
          const normalizedCode = status.name.toUpperCase().replace(/\s+/g, '_');
          return {
            value: normalizedCode, // Use code as value (e.g., "IN_PROGRESS")
            label: status.name, // Use display name (e.g., "In Progress")
            id: status.id, // Store MongoDB ID
          };
        });
        
        if (apiStatusOptions.length > 0) {
          setStatusOptions(apiStatusOptions);
        }
        
        // Create a map of status values to MongoDB IDs
        // Map both the status value (e.g., "IN_PROGRESS") and label (e.g., "In Progress") to the ID
        const idMap = new Map<string, string>();
        statuses.forEach(status => {
          const normalizedCode = status.name.toUpperCase().replace(/\s+/g, '_');
          
          // Map status name directly
          idMap.set(status.name, status.id);
          
          // Map normalized status codes (e.g., "IN_PROGRESS")
          idMap.set(normalizedCode, status.id);
          
          // Map common variations
          const variations: Record<string, string> = {
            'NOT_STARTED': 'Not Started',
            'IN_PROGRESS': 'In Progress',
            'AWAITING_FEEDBACK': 'Awaiting Feedback',
            'READY_FOR_PRODUCTION': 'Ready for Production',
            'ON_HOLD': 'On Hold',
            'COMPLETE': 'Complete',
            'COMPLETED': 'Complete',
          };
          
          Object.entries(variations).forEach(([code, name]) => {
            if (status.name === name || status.name.toUpperCase().replace(/\s+/g, '_') === code) {
              idMap.set(code, status.id);
              idMap.set(name, status.id);
            }
          });
        });
        
        setStatusIdMap(idMap);
        console.log('Task statuses loaded:', statuses.length, 'ID map entries:', idMap.size, 'Options:', apiStatusOptions.length);
      } catch (error) {
        console.error('Error loading task statuses:', error);
      }
    };

    if (open) {
      loadTaskStatuses();
    }
  }, [open]);

  // Load available users
  useEffect(() => {
    const loadUsers = async () => {
      try {
        console.log('Loading users for dropdown...');
        const users = await getEmployeesForDropdown();
        console.log('Loaded users:', users);
        setAvailableUsers(users || []);
        console.log('Available users count:', users?.length || 0);
      } catch (error) {
        console.error('Error loading users:', error);
        setAvailableUsers([]);
      }
    };

    if (open) {
      loadUsers();
    }
  }, [open]);

  // Once users are loaded, convert any name entries in formData.assignees/followers to IDs
  useEffect(() => {
    if (!open || availableUsers.length === 0) return;

    const resolveToIds = (values: string[]) => {
      return values.map((val) => {
        // If already looks like an ID that exists, keep it
        const byId = availableUsers.find((u) => (u._id || u.id) === val);
        if (byId) return val;
        // Otherwise, try resolve by name/email
        const byName = availableUsers.find((u) => u.name === val || u.email === val);
        return byName ? (byName._id || byName.id) : val; // keep as-is if not resolvable
      });
    };

    setFormData((prev) => ({
      ...prev,
      assignees: resolveToIds(prev.assignees),
      followers: resolveToIds(prev.followers),
    }));
  }, [open, availableUsers]);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAddTag = () => {
    if (formData.newTag.trim() && !formData.tags.includes(formData.newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, prev.newTag.trim()],
        newTag: ''
      }));
    }
  };

  const handleRemoveTag = (tagToRemove: TagLike) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => {
        if (typeof tag === 'string' && typeof tagToRemove === 'string') {
          return tag !== tagToRemove;
        }
        // Handle object comparison
        const tagId = typeof tag === 'string' ? tag : (tag?._id || tag?.id || tag?.name || tag?.title);
        const removeId = typeof tagToRemove === 'string' ? tagToRemove : (tagToRemove?._id || tagToRemove?.id || tagToRemove?.name || tagToRemove?.title);
        return tagId !== removeId;
      })
    }));
  };

  const handleSubmit = async () => {
    if (!task?.id) return;

    // Check permissions
    if (!hasPermission('project_management.task.edit')) {
      toast({
        title: "Access Denied",
        description: "You don't have permission to edit tasks.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);
    try {
      const updateData: Partial<CreateTaskRequest> = {
        title: formData.title,
        description: formData.description,
        assignees: formData.assignees,
        followers: formData.followers,
        startDate: formData.startDate,
        dueDate: formData.dueDate || undefined,
        priority: formData.priority as 'Low' | 'Medium' | 'High',
        tags: formData.tags
          .map(tagToString)
          .filter((tag): tag is string => Boolean(tag)),
        estimateHours: formData.estimateHours,
        url: formData.url || undefined
      };

      const updatedTask = await taskService.updateTask(task.id, updateData);
      
      // Update status separately if it changed
      // API expects MongoDB ID, not status value
      let finalUpdatedTask = updatedTask;
      let statusUpdateFailed = false;
      let statusErrorMessage = '';
      
      if (formData.status !== task.status) {
        try {
          // Get status ID from the map
          const statusId = statusIdMap.get(formData.status);
          
          if (!statusId) {
            console.error('Status ID not found for status:', formData.status, 'Available mappings:', Array.from(statusIdMap.entries()));
            statusUpdateFailed = true;
            statusErrorMessage = `Status ID not found for "${formData.status}". The task has been updated but the status could not be changed.`;
          } else {
            console.log('Updating task status:', formData.status, 'to ID:', statusId);
            finalUpdatedTask = await taskService.updateTaskStatus(task.id, statusId);
          }
        } catch (statusError: any) {
          console.error('Error updating task status:', statusError);
          statusUpdateFailed = true;
          statusErrorMessage = statusError.response?.data?.message || "The task was updated but the status change failed.";
        }
      }

      // Show appropriate toast message
      if (statusUpdateFailed) {
        toast({
          title: "⚠️ Task Updated with Warning",
          description: `${statusErrorMessage} Please try updating the status again.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "✅ Task Updated",
          description: "Task has been updated successfully.",
        });
      }

      // Notify parent component with the actual updated task data from server
      if (onTaskUpdate) {
        // Map the server response to the expected format
        const mappedUpdates = {
          name: finalUpdatedTask.title, // Map title to name
          description: finalUpdatedTask.description,
          status: finalUpdatedTask.status,
          priority: finalUpdatedTask.priority,
          startDate: finalUpdatedTask.startDate,
          dueDate: finalUpdatedTask.dueDate,
          estimatedHours: finalUpdatedTask.estimateHours,
          url: finalUpdatedTask.url,
          tags: finalUpdatedTask.tags,
          assignees: finalUpdatedTask.assignees,
          followers: finalUpdatedTask.followers
        };
        onTaskUpdate(task.id, mappedUpdates);
      }

      // Close dialog after successful update
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error updating task:', error);
      toast({
        title: "❌ Update Failed",
        description: error.response?.data?.message || "Failed to update task. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!task) return null;

  // Check if user has permission to edit tasks
  if (!hasPermission('project_management.task.edit')) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5" />
            Edit Task: {task.name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Title */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Task Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              placeholder="Enter task title"
              disabled={isLoading}
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Enter task description"
              rows={3}
              disabled={isLoading}
            />
          </div>

          {/* Status and Priority */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Status</label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange('status', value)}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Priority</label>
              <Select
                value={formData.priority}
                onValueChange={(value) => handleInputChange('priority', value)}
                disabled={isLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select priority" />
                </SelectTrigger>
                <SelectContent>
                  {priorityOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className="flex items-center gap-2">
                        <Badge className={option.color}>
                          {option.label}
                        </Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Start Date *
              </label>
              <Input
                type="date"
                value={formData.startDate}
                onChange={(e) => handleInputChange('startDate', e.target.value)}
                disabled={isLoading}
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Due Date
              </label>
              <Input
                type="date"
                value={formData.dueDate}
                onChange={(e) => handleInputChange('dueDate', e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Estimated Hours */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Estimated Hours
            </label>
            <Input
              type="number"
              value={formData.estimateHours ?? ''}
              onChange={(e) => {
                const next = e.target.value === '' ? 0 : Number(e.target.value);
                handleInputChange('estimateHours', isNaN(next) ? 0 : next);
              }}
              placeholder="0"
              min="0"
              step="0.5"
              disabled={isLoading}
            />
          </div>

          {/* URL */}
          <div className="space-y-2">
            <label className="text-sm font-medium">URL</label>
            <Input
              value={formData.url}
              onChange={(e) => handleInputChange('url', e.target.value)}
              placeholder="https://example.com"
              disabled={isLoading}
            />
          </div>

          {/* Assignees */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Assignees
            </label>
              <Select
              onValueChange={(value) => {
                if (!formData.assignees.includes(value)) {
                  handleInputChange('assignees', [...formData.assignees, value]);
                }
              }}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select assignees" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((user) => {
                  const userId = user._id || user.id;
                  const userName = user.name || user.email || 'Unknown User';
                  return (
                    <SelectItem key={userId} value={userId}>
                      {userName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {/* Selected Assignees */}
            {formData.assignees.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.assignees.map((assigneeId) => {
                  const user = availableUsers.find(u => (u._id || u.id) === assigneeId);
                  const userName = user ? (user.name || user.email || 'Unknown User') : assigneeId; // show raw if unresolved
                  return (
                    <Badge key={assigneeId} variant="secondary" className="flex items-center gap-1">
                      {userName}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => handleInputChange('assignees', formData.assignees.filter(id => id !== assigneeId))}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {/* Followers */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <User className="h-4 w-4" />
              Followers
            </label>
            <Select
              onValueChange={(value) => {
                if (!formData.followers.includes(value)) {
                  handleInputChange('followers', [...formData.followers, value]);
                }
              }}
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select followers" />
              </SelectTrigger>
              <SelectContent>
                {availableUsers.map((user) => {
                  const userId = user._id || user.id;
                  const userName = user.name || user.email || 'Unknown User';
                  return (
                    <SelectItem key={userId} value={userId}>
                      {userName}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            {/* Selected Followers */}
            {formData.followers.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.followers.map((followerId) => {
                  const user = availableUsers.find(u => (u._id || u.id) === followerId);
                  const userName = user ? (user.name || user.email || 'Unknown User') : followerId; // show raw if unresolved
                  return (
                    <Badge key={followerId} variant="outline" className="flex items-center gap-1">
                      {userName}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => handleInputChange('followers', formData.followers.filter(id => id !== followerId))}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2">
              <Tag className="h-4 w-4" />
              Tags
            </label>
            <div className="flex gap-2">
              <Input
                value={formData.newTag}
                onChange={(e) => handleInputChange('newTag', e.target.value)}
                placeholder="Add tag"
                disabled={isLoading}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddTag();
                  }
                }}
              />
              <Button 
                type="button" 
                variant="outline" 
                onClick={handleAddTag}
                disabled={isLoading || !formData.newTag.trim()}
              >
                Add
              </Button>
            </div>
            
            {/* Selected Tags */}
            {formData.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.tags.map((tag, tagIndex) => {
                  const tagName = typeof tag === 'string' ? tag : (tag?.name || tag?.title || String(tag));
                  const tagKey = typeof tag === 'string' ? tag : (tag?._id || tag?.id || tagIndex);
                  return (
                    <Badge key={tagKey} variant="secondary" className="flex items-center gap-1">
                      {tagName}
                      <X 
                        className="h-3 w-3 cursor-pointer" 
                        onClick={() => handleRemoveTag(tag)}
                      />
                    </Badge>
                  );
                })}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isLoading || !formData.title.trim() || !formData.startDate}
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
